// src/pages/CourseDetails.jsx
import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  BookOpen,
  ArrowLeft,
  PlayCircle,
  Clock,
  CheckCircle,
  AlertCircle,
} from "lucide-react";

// ✅ Shared API base (same pattern as other pages)
const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_API_BASE?.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE &&
    process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  "";

/**
 * Brand-aligned “fun rainbow”:
 * - Every gradient is anchored with ORANGE on one side (enterprise + brand)
 * - Secondary hue adds variety while staying premium
 */
const colorMap = {
  discover: { color: "from-orange-600 to-amber-500" },
  ugunduzi: { color: "from-orange-600 to-emerald-500" },
  "daniel-revelation": { color: "from-orange-600 to-indigo-500" },
  health: { color: "from-orange-600 to-cyan-500" },
  "focus-family": { color: "from-orange-600 to-rose-500" },
  family: { color: "from-orange-600 to-rose-500" }, // alias
};
const fallbackColor = { color: "from-orange-600 to-amber-600" };

// Minimal fallback (used only if backend is unreachable)
const fallback = {
  discover: {
    title: "Discover",
    description: "A comprehensive journey through Bible foundations and prophecy.",
    total_lessons: 0,
    duration: "—",
    level: "Beginner",
    is_published: true,
  },
  ugunduzi: {
    title: "Ugunduzi",
    description: "Swahili lessons on faith and discovery.",
    total_lessons: 0,
    duration: "—",
    level: "Beginner",
    is_published: true,
  },
  "daniel-revelation": {
    title: "Daniel & Revelation",
    description: "In-depth study of prophetic books.",
    total_lessons: 0,
    duration: "—",
    level: "Intermediate",
    is_published: true,
  },
  health: {
    title: "Health",
    description: "Biblical health principles.",
    total_lessons: 0,
    duration: "—",
    level: "Beginner",
    is_published: false,
  },
  "focus-family": {
    title: "Focus on the Family",
    description: "Biblical principles for healthy family relationships.",
    total_lessons: 0,
    duration: "—",
    level: "All Levels",
    is_published: false,
  },
  family: {
    title: "Focus on the Family",
    description: "Biblical principles for healthy family relationships.",
    total_lessons: 0,
    duration: "—",
    level: "All Levels",
    is_published: false,
  },
};

function clamp(n, a, b) {
  return Math.min(b, Math.max(a, n));
}

export default function CourseDetails() {
  const { courseId } = useParams();

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [course, setCourse] = useState(null);
  const [fromFallback, setFromFallback] = useState(false);

  // Progress state
  const [completedSet, setCompletedSet] = useState(() => new Set());
  const [nextLesson, setNextLesson] = useState(1);

  useEffect(() => {
    let mounted = true;

    async function load() {
      setLoading(true);
      setErr("");
      setFromFallback(false);

      try {
        // 1) Course metadata
        const res = await fetch(`${API_BASE}/api/content/courses/${courseId}`, {
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json().catch(() => ({}));
        const data = json?.data || json;
        if (!data || !data.slug) throw new Error("Unexpected response shape");
        if (mounted) setCourse(data);

        // 2) Progress for this user & course
        try {
          const p = await fetch(`${API_BASE}/api/progress/me/${courseId}`, {
            credentials: "include",
          });
          if (p.ok) {
            const pj = await p.json().catch(() => ({}));
            const comp = Array.isArray(pj?.data?.lessonsCompleted)
              ? pj.data.lessonsCompleted
              : Array.isArray(pj?.lessonsCompleted)
              ? pj.lessonsCompleted
              : [];
            const nx =
              typeof pj?.data?.nextLesson === "number"
                ? pj.data.nextLesson
                : typeof pj?.nextLesson === "number"
                ? pj.nextLesson
                : comp.length
                ? Math.max(...comp) + 1
                : 1;

            if (mounted) {
              setCompletedSet(new Set(comp));
              setNextLesson(nx);
            }
          }
        } catch {
          /* non-fatal */
        }
      } catch (e) {
        console.error("Course details load error:", e);
        const hint =
          e?.message?.includes("Failed to fetch") || e?.name === "TypeError"
            ? "Cannot reach backend. If this is CORS, include your frontend origin in CORS_ORIGIN and restart the server."
            : "";

        if (mounted) {
          setErr(`Could not load course details. ${hint}`.trim());

          // Fallback minimal local map (no totals)
          const fb = fallback[courseId];
          if (fb) {
            setCourse({ slug: courseId, ...fb });
            setCompletedSet(new Set());
            setNextLesson(1);
            setFromFallback(true);
          } else {
            setCourse({
              slug: courseId,
              title: courseId?.replace(/-/g, " ") || "Course",
              description: "Course details coming soon.",
              total_lessons: 0,
              duration: "—",
              level: "—",
              is_published: false,
            });
            setFromFallback(true);
          }
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [courseId]);

  const ui = useMemo(() => {
    if (!course) return null;
    const slug = course.slug || courseId;
    const colors = colorMap[slug] || colorMap[courseId] || fallbackColor;

    const lessons = Number(course.total_lessons) || 0;
    const completedCount = [...completedSet].filter(
      (n) => n >= 1 && (lessons ? n <= lessons : true)
    ).length;

    const completionPct = lessons ? clamp(Math.round((completedCount / lessons) * 100), 0, 100) : 0;

    const published = !!(course.is_published ?? course.status === "published");

    const safeNext = lessons ? clamp(Number(nextLesson || 1), 1, lessons) : Number(nextLesson || 1);

    return {
      color: colors.color,
      title: course.title || slug,
      description: course.description || "",
      lessons,
      duration: course.duration || "—",
      level: course.level || "All Levels",
      published,
      completedCount,
      completionPct,
      safeNext,
    };
  }, [course, courseId, completedSet, nextLesson]);

  const canStart =
    ui &&
    ui.published &&
    ui.lessons > 0 && // only when we actually know lesson count
    !err && // only when backend loaded cleanly
    !fromFallback;

  const startLesson = ui?.safeNext || 1;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900">
      <Navbar />

      <main className="flex-1 pt-24 pb-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Top row */}
          <div className="flex items-center justify-between gap-3 mb-6">
            <Link
              to="/courses"
              className="inline-flex items-center gap-2 text-sm font-extrabold text-orange-700 hover:text-orange-800 dark:text-orange-200 dark:hover:text-orange-100"
            >
              <ArrowLeft size={18} />
              Back to Courses
            </Link>

            {ui?.published ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-extrabold dark:bg-emerald-500/10 dark:text-emerald-200">
                <CheckCircle size={14} />
                Published
              </span>
            ) : (
              <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 text-slate-700 px-3 py-1 text-xs font-extrabold dark:bg-slate-900 dark:text-slate-200">
                Not published
              </span>
            )}
          </div>

          {/* Loading / Error */}
          {loading && (
            <div className="mb-6 inline-flex items-center gap-2 rounded-2xl border border-orange-200 bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-800 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-200">
              <span className="animate-spin rounded-full h-4 w-4 border-2 border-orange-600 border-t-transparent" />
              Loading…
            </div>
          )}

          {!!err && (
            <div className="mb-6 flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">
              <AlertCircle size={18} className="mt-0.5 flex-shrink-0" />
              <div className="min-w-0">{err}</div>
            </div>
          )}

          {ui && (
            <>
              {/* HERO */}
              <section className="relative overflow-hidden rounded-3xl border border-slate-200 bg-white/80 backdrop-blur shadow-xl dark:bg-slate-950/60 dark:border-slate-800">
                <div className={`absolute inset-0 opacity-[0.10] bg-gradient-to-br ${ui.color}`} />
                <div className="absolute -top-24 -right-24 h-72 w-72 rounded-full bg-orange-300/30 blur-3xl dark:bg-orange-500/15" />
                <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-amber-300/30 blur-3xl dark:bg-amber-500/15" />

                <div className="relative p-6 sm:p-8">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                    <div className="min-w-0">
                      <div className="flex items-center gap-3">
                        <div className={`h-12 w-12 rounded-2xl bg-gradient-to-br ${ui.color} flex items-center justify-center shadow-md`}>
                          <BookOpen className="text-white" size={22} />
                        </div>
                        <div className="min-w-0">
                          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white truncate">
                            {ui.title}
                          </h1>
                          <p className="text-slate-600 dark:text-slate-300 mt-1">
                            {ui.description}
                          </p>
                        </div>
                      </div>

                      {/* Meta chips */}
                      <div className="mt-5 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-2 rounded-2xl border border-orange-200 bg-orange-50 px-3 py-1.5 text-xs font-extrabold text-orange-700 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-200">
                          Level: {ui.level}
                        </span>

                        <span className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-extrabold text-slate-700 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200">
                          <Clock size={14} />
                          Duration: {ui.duration}
                        </span>

                        <span className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-extrabold text-slate-700 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200">
                          Lessons: {ui.lessons || "—"}
                        </span>

                        <span className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-extrabold text-slate-700 dark:bg-slate-900 dark:border-slate-700 dark:text-slate-200">
                          Completed: {ui.completedCount}
                        </span>
                      </div>

                      {/* Progress bar */}
                      <div className="mt-5">
                        <div className="flex items-center justify-between text-xs font-extrabold text-slate-600 dark:text-slate-300 mb-2">
                          <span>Progress</span>
                          <span>{ui.lessons ? `${ui.completionPct}%` : "—"}</span>
                        </div>
                        <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden dark:bg-slate-800">
                          <div
                            className={`h-2 rounded-full bg-gradient-to-r ${ui.color}`}
                            style={{ width: `${ui.lessons ? ui.completionPct : 0}%` }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* CTA stack */}
                    <div className="w-full lg:w-auto flex flex-col sm:flex-row lg:flex-col gap-3">
                      {canStart ? (
                        <Link
                          to={`/courses/${encodeURIComponent(courseId)}/lessons/${startLesson}`}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-5 py-3 font-extrabold shadow-lg hover:opacity-95 transition"
                        >
                          <PlayCircle size={20} />
                          {ui.completedCount ? `Continue (Lesson ${startLesson})` : "Start learning"}
                        </Link>
                      ) : (
                        <Link
                          to={`/courses/${encodeURIComponent(courseId)}/lessons/1`}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-5 py-3 font-extrabold shadow-lg hover:opacity-95 transition"
                        >
                          <PlayCircle size={20} />
                          Preview Lesson 1
                        </Link>
                      )}

                      <Link
                        to="/courses"
                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 font-extrabold text-slate-900 hover:bg-slate-50 transition dark:bg-slate-900 dark:border-slate-700 dark:text-white dark:hover:bg-slate-800"
                      >
                        Browse courses
                      </Link>
                    </div>
                  </div>
                </div>
              </section>

              {/* CONTENT */}
              <section className="mt-8 grid lg:grid-cols-3 gap-6">
                {/* Lessons */}
                <div className="lg:col-span-2">
                  <div className="rounded-3xl border border-slate-200 bg-white shadow-lg p-6 dark:bg-slate-950/60 dark:border-slate-800">
                    <div className="flex items-center justify-between gap-3 mb-5">
                      <h2 className="text-lg sm:text-xl font-extrabold text-slate-900 dark:text-white">
                        Lessons
                      </h2>

                      {ui.lessons > 0 ? (
                        <span className="text-xs font-extrabold text-slate-500 dark:text-slate-400">
                          Next: Lesson {startLesson}
                        </span>
                      ) : null}
                    </div>

                    {ui.lessons > 0 ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {Array.from({ length: ui.lessons }, (_, i) => i + 1).map((n) => {
                          const isDone = completedSet.has(n);
                          const isNext = n === startLesson && !isDone;

                          return (
                            <div
                              key={n}
                              className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition dark:bg-slate-900/60 dark:border-slate-800"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`inline-flex h-8 w-8 items-center justify-center rounded-2xl text-sm font-extrabold border ${
                                        isDone
                                          ? "bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-200"
                                          : "bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-950 dark:border-slate-800 dark:text-slate-200"
                                      }`}
                                    >
                                      {isDone ? <CheckCircle size={18} /> : n}
                                    </span>
                                    <p className="font-extrabold text-slate-900 dark:text-white truncate">
                                      Lesson {n}
                                    </p>
                                  </div>

                                  <div className="mt-2 flex flex-wrap gap-2">
                                    {isNext && (
                                      <span className="inline-flex items-center rounded-full bg-orange-50 text-orange-700 px-3 py-1 text-xs font-extrabold border border-orange-200 dark:bg-orange-500/10 dark:border-orange-500/30 dark:text-orange-200">
                                        Next up
                                      </span>
                                    )}
                                    {isDone && (
                                      <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-extrabold border border-emerald-200 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-200">
                                        Completed
                                      </span>
                                    )}
                                  </div>
                                </div>

                                <div className={`h-2 w-16 rounded-full bg-gradient-to-r ${ui.color} opacity-70`} />
                              </div>

                              <div className="mt-4 grid grid-cols-2 gap-3">
                                <Link
                                  to={`/courses/${encodeURIComponent(courseId)}/lessons/${n}`}
                                  className="inline-flex items-center justify-center rounded-2xl bg-slate-900 text-white px-4 py-3 font-extrabold hover:opacity-90 transition dark:bg-white dark:text-slate-900"
                                >
                                  Open
                                </Link>
                                <Link
                                  to={`/courses/${encodeURIComponent(courseId)}/lessons/${n}/test`}
                                  className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-3 font-extrabold text-slate-900 hover:bg-slate-50 transition dark:bg-slate-950 dark:border-slate-800 dark:text-white dark:hover:bg-slate-900"
                                >
                                  Quiz
                                </Link>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="rounded-3xl border border-orange-200 bg-orange-50 p-6 dark:bg-orange-500/10 dark:border-orange-500/30">
                        <p className="text-orange-800 dark:text-orange-200 font-semibold">
                          📚 Lessons for this course will be available soon.
                          {fromFallback ? " (We couldn’t fully load details from the server.)" : ""}
                        </p>
                        <div className="mt-4">
                          <Link
                            to={`/courses/${encodeURIComponent(courseId)}/lessons/1`}
                            className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-5 py-3 font-extrabold shadow-lg hover:opacity-95 transition"
                          >
                            <PlayCircle size={20} />
                            Preview Lesson 1
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Sidebar */}
                <aside className="space-y-6">
                  <div className="rounded-3xl border border-slate-200 bg-white shadow-lg p-6 dark:bg-slate-950/60 dark:border-slate-800">
                    <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">
                      Your progress
                    </h3>
                    <p className="text-slate-600 dark:text-slate-300 mt-1">
                      Keep going—finish a lesson, take the quiz, and your progress saves automatically.
                    </p>

                    <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 dark:bg-slate-900 dark:border-slate-700">
                      <div className="flex items-center justify-between text-sm font-extrabold text-slate-700 dark:text-slate-200">
                        <span>Completion</span>
                        <span>{ui.lessons ? `${ui.completionPct}%` : "—"}</span>
                      </div>
                      <div className="mt-2 h-2 w-full rounded-full bg-slate-100 overflow-hidden dark:bg-slate-800">
                        <div
                          className={`h-2 rounded-full bg-gradient-to-r ${ui.color}`}
                          style={{ width: `${ui.lessons ? ui.completionPct : 0}%` }}
                        />
                      </div>

                      <div className="mt-3 text-xs font-semibold text-slate-500 dark:text-slate-400">
                        {ui.lessons ? `${ui.completedCount} of ${ui.lessons} lessons completed` : "Lesson count not available yet"}
                      </div>
                    </div>

                    <div className="mt-4 flex flex-col gap-3">
                      {canStart ? (
                        <Link
                          to={`/courses/${encodeURIComponent(courseId)}/lessons/${startLesson}`}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-5 py-3 font-extrabold shadow-lg hover:opacity-95 transition"
                        >
                          <PlayCircle size={20} />
                          Continue (Lesson {startLesson})
                        </Link>
                      ) : (
                        <Link
                          to={`/courses/${encodeURIComponent(courseId)}/lessons/1`}
                          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-5 py-3 font-extrabold shadow-lg hover:opacity-95 transition"
                        >
                          <PlayCircle size={20} />
                          Preview Lesson 1
                        </Link>
                      )}

                      <Link
                        to="/dashboard"
                        className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 font-extrabold text-slate-900 hover:bg-slate-50 transition dark:bg-slate-900 dark:border-slate-700 dark:text-white dark:hover:bg-slate-800"
                      >
                        Back to dashboard
                      </Link>
                    </div>
                  </div>
                </aside>
              </section>
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
